// 「排除特定時段」的可讀預覽（CNCY-10468）
//
// 🔴 monthly／yearly 的「起始 X 日 HH:mm ／ 結束 Y 日 HH:mm」兩列選單，實際語意是
// **一段連續區間**，不是「每個月的那幾天、每天這個時段」。商家想表達「每天 13:00
// 之後不營業」而設成「1 日 13:00 ~ 31 日 13:00」⇒ 整個月都被排除，只有月初與月底
// 兩個尾巴逃得掉。UAT 實測就是這樣讓取貨 C 端整片時段不可預約，而後台的任何頁面
// 都看不出哪裡設錯（CNCY-10467）。
//
// 本檔只做「規則 → 一句人看得懂的話」，不做任何判定。真正的展開在後端
// services/businessTime/excludeTimeRules.js，兩邊的語意必須一致：
//   - 區間是連續的 [start, end)
//   - 「幾號」在天數不足的月份夾到當月最後一天（CNCY-10466）
//
// ⚠️ 文案沿用 AddPeriodTimes.vue 既有的硬寫繁中（該元件的 displayText 就是這樣），
// 不新增 i18n key —— 那個元件整份都還沒接 i18n，只補這一段會變成半套。

import dayjs from '@/lib/dayjs'

// 0 = 週日，與 weeklySetting.week／後端 periodicityAt 同一套
const WEEK_LABELS = ['週日', '週一', '週二', '週三', '週四', '週五', '週六']

// 天數最少的月份。endDay 大於它就代表「有些月份會被夾到月底」
const SHORTEST_MONTH_DAYS = 28

const pad2 = (value) => String(value).padStart(2, '0')

/**
 * 時間欄位正規化成 HH:mm。
 *
 * 表單裡是 el-time-select 的 'HH:mm' 字串，但從後端載回來的是分鐘數
 * （useBusinessTime 會轉，轉之前也可能被別處直接餵進來），兩種都要吃得下。
 *
 * @param {string|number} value 時間
 * @return {string} HH:mm
 */
export const normalizeTime = (value) => {
  if (value === null || value === undefined || value === '') return '00:00'

  if (typeof value === 'number') {
    const minute = Math.max(0, Math.min(24 * 60, value))
    return `${pad2(Math.floor(minute / 60))}:${pad2(minute % 60)}`
  }

  const [hour = '0', minute = '0'] = String(value).split(':')
  return `${pad2(Number(hour))}:${pad2(Number(minute))}`
}

const toMinutes = (value) => {
  const [hour, minute] = normalizeTime(value).split(':')
  return Number(hour) * 60 + Number(minute)
}

const reversedNote = '結束時間不晚於開始時間，這條規則不會生效。'

const shortMonthNote =
  '天數不足的月份會夾到當月最後一天（例如 2 月的 31 日 ＝ 28 或 29 日）。'

/**
 * 連續區間的提醒。跨越一天以上時一定要講，這是最容易誤設的地方。
 *
 * @param {string} startText 起點的可讀文字
 * @param {string} endText 終點的可讀文字
 * @return {string} 提醒文字
 */
const continuousNote = (startText, endText) =>
  `這是一段連續的排除區間：從 ${startText} 一路到 ${endText} 之間全部不營業，` +
  '不是每天只排除這個時段。'

/**
 * once 型：一段指定的日期時間區間。
 *
 * ⚠️ dateRange 的值是**店家牆上時間**字串（`YYYY-MM-DD HH:mm:ss`，picker 的
 * value-format），不是絕對時間戳（CNCY-10430）。`dayjs(裸字串)` 不做時區換算，
 * 印出來就是商家當初選的那組數字 —— 不同時區的員工看到的會是同一個時間。
 *
 * @param {object} setting onceSetting
 * @return {object} { text, notes }
 */
const describeOnce = (setting) => {
  const range = (setting && setting.dateRange) || []
  const [startAt, endAt] = range

  if (!startAt || !endAt) return { text: '尚未選擇日期區間', notes: [] }

  const format = (value) => dayjs(value).format('YYYY-MM-DD HH:mm')
  const notes = []
  if (!dayjs(endAt).isAfter(dayjs(startAt))) notes.push(reversedNote)

  return { text: `${format(startAt)} ~ ${format(endAt)}（只發生一次）`, notes }
}

/**
 * weekly 型：一定落在同一天之內，不會有連續區間的誤解。
 *
 * @param {object} setting weeklySetting
 * @return {object} { text, notes }
 */
const describeWeekly = (setting) => {
  const week = (setting && setting.week) || 0
  const start = normalizeTime(setting && setting.startTime)
  const end = normalizeTime(setting && setting.endTime)

  const notes = []
  if (toMinutes(end) <= toMinutes(start)) notes.push(reversedNote)

  return { text: `每週${WEEK_LABELS[week].slice(1)} ${start} ~ ${end}`, notes }
}

/**
 * monthly 型：跨日就是連續區間。
 *
 * @param {object} setting monthlySetting
 * @return {object} { text, notes }
 */
const describeMonthly = (setting) => {
  const startDay = (setting && setting.startDay) || 1
  const endDay = (setting && setting.endDay) || 1
  const start = normalizeTime(setting && setting.startTime)
  const end = normalizeTime(setting && setting.endTime)

  const notes = []
  if (endDay > SHORTEST_MONTH_DAYS) notes.push(shortMonthNote)

  if (startDay === endDay) {
    if (toMinutes(end) <= toMinutes(start)) notes.push(reversedNote)
    return { text: `每月 ${startDay} 日 ${start} ~ ${end}`, notes }
  }

  if (endDay < startDay) {
    return { text: `每月 ${startDay} 日 ${start} ~ ${endDay} 日 ${end}`, notes: [reversedNote] }
  }

  notes.unshift(continuousNote(`${startDay} 日 ${start}`, `${endDay} 日 ${end}`))

  return {
    text: `每月 ${startDay} 日 ${start} 起，連續排除到 ${endDay} 日 ${end}（約 ${endDay - startDay} 天）`,
    notes,
  }
}

/**
 * yearly 型：跨月或跨日都是連續區間。
 *
 * @param {object} setting yearlySetting
 * @return {object} { text, notes }
 */
const describeYearly = (setting) => {
  const startMonth = (setting && setting.startMonth) || 1
  const startDay = (setting && setting.startDay) || 1
  const endMonth = (setting && setting.endMonth) || 1
  const endDay = (setting && setting.endDay) || 1
  const start = normalizeTime(setting && setting.startTime)
  const end = normalizeTime(setting && setting.endTime)

  const startText = `${startMonth} 月 ${startDay} 日 ${start}`
  const endText = `${endMonth} 月 ${endDay} 日 ${end}`

  const notes = []
  if (endDay > SHORTEST_MONTH_DAYS) notes.push(shortMonthNote)

  const sameDay = startMonth === endMonth && startDay === endDay
  if (sameDay) {
    if (toMinutes(end) <= toMinutes(start)) notes.push(reversedNote)
    return { text: `每年 ${startMonth} 月 ${startDay} 日 ${start} ~ ${end}`, notes }
  }

  const reversed =
    endMonth < startMonth || (startMonth === endMonth && endDay < startDay)
  if (reversed) {
    return { text: `每年 ${startText} ~ ${endText}`, notes: [reversedNote] }
  }

  notes.unshift(continuousNote(startText, endText))

  return { text: `每年 ${startText} 起，連續排除到 ${endText}`, notes }
}

/**
 * ★ 把一條排除規則講成一句人看得懂的話。
 *
 * @param {object} rule 表單裡的一條排除規則
 * @return {object} { text, notes } notes 是要提醒商家的事，可能是空陣列
 */
export const describeExcludeTimeRule = (rule) => {
  if (!rule) return { text: '', notes: [] }

  switch (rule.type) {
  case 'once':
    return describeOnce(rule.onceSetting)
  case 'weekly':
    return describeWeekly(rule.weeklySetting)
  case 'monthly':
    return describeMonthly(rule.monthlySetting)
  case 'yearly':
    return describeYearly(rule.yearlySetting)
  default:
    return { text: '', notes: [] }
  }
}
